import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {v4 as uuid} from "uuid";
import {validateJSON, validateJSONNow} from "../web/validation";
import {DatabaseCode, toDatabaseCode} from "./database-code";
import {getDatabaseCodeTransformer} from "./transfomers/transformers";
import {clone} from "lodash";
import {Entity, EntityObject, getRequiredEntityId, isEntity} from "./entity";
import {FreightTypeEntity} from "./freight-type";
import {UnitOfMeasureEntity} from "./unit-of-measure";
import {RegionEntity} from "./region";
import {Exportable} from "./exportable";

export type MeterType = DatabaseCode;

const flow: MeterType = {
  code: 'FLOW',
  displayValue: {
    id: 'ots.ui.entity.meter.type.flow',
    defaultMessage: 'Flow',
  },
};

const allocation: MeterType = {
  code: 'ALLOCATION',
  displayValue: {
    id: 'ots.ui.entity.meter.type.allocation',
    defaultMessage: 'Allocation',
  },
};

const lact: MeterType = {
  code: 'LACT',
  displayValue: {
    id: 'ots.ui.entity.meter.type.lact',
    defaultMessage: 'LACT',
  },
};

export const meterType = {
  flow,
  allocation,
  lact,
};

export const meterTypes = Object.values(meterType);

export const toMeterType = (code: string): MeterType => toDatabaseCode(code, meterTypes)

export type FlowMeterType = DatabaseCode;

const hub: FlowMeterType = {
  code: 'HUB',
  displayValue: {
    id: 'ots.ui.entity.meter.flowmetertype.hub',
    defaultMessage: 'Hub',
  },
};

const check: FlowMeterType = {
  code: 'CHECK',
  displayValue: {
    id: 'ots.ui.entity.meter.flowmetertype.check',
    defaultMessage: 'Check',
  },
};

export const flowMeterType = {
  hub,
  check,
}

export const flowMeterTypes = Object.values(flowMeterType);

export const toFlowMeterType = (code: string): FlowMeterType => toDatabaseCode(code, flowMeterTypes)

export const MeterValidator = t.intersection([
  t.type({
    id: t.string,
    regionId: t.string,
    name: t.string,
    type: t.string,
    unitOfMeasure: t.string,
  }),
  t.partial({
    flowMeterType: t.string,
    barcode: t.string,
    freightTypeId: t.string,
    freightTypeName: t.string,
    objectId: t.string,
    objectType: t.string,
    objectName: t.string,
    objectFreightTypeId: t.string,
    objectFreightTypeName: t.string,
    doNotUse: t.boolean,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const MeterArrayValidator = t.array(MeterValidator);

export type Meter = t.TypeOf<typeof MeterValidator>;
export type MeterArray = t.TypeOf<typeof MeterArrayValidator>;

@Exclude()
export class MeterEntity implements Entity, Exportable {
  @Expose()
  id!: string;
  @Expose()
  name!: string;
  @Expose()
  regionId!: string;
  @Expose()
  @Transform(getDatabaseCodeTransformer(toMeterType))
  type!: MeterType;
  @Expose()
  unitOfMeasure!: string;
  @Expose()
  @Transform(getDatabaseCodeTransformer(toFlowMeterType))
  flowMeterType?: FlowMeterType;
  @Expose()
  barcode?: string;
  @Expose()
  freightTypeId?: string;
  @Expose()
  freightTypeName?: string;
  @Expose()
  objectId?: string;
  @Expose()
  objectType?: string;
  @Expose()
  objectName?: string;
  @Expose()
  objectFreightTypeId?: string;
  @Expose()
  objectFreightTypeName?: string;
  @Expose()
  doNotUse = false
  @Expose()
  createDttm?: number;
  @Expose()
  lastModifiedDttm?: number;

  readonly entityType = 'METER';

  get displayName(): Readonly<string> {
    return this.name;
  }

  /**
   * Constructor for MeterEntity.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static builder(id: string = uuid()): NameStep {
    return new MeterBuilder(id);
  }

  static create(builder: MeterBuilder): MeterEntity {
    const entity = new MeterEntity();
    entity.id = builder.id;
    entity.name = builder.name;
    entity.regionId = builder.regionId;
    entity.type = builder.type;
    entity.unitOfMeasure = builder.unitOfMeasure;
    entity.flowMeterType = builder.flowMeterType;
    entity.barcode = builder.barcode;
    entity.freightTypeId = builder.freightTypeId;
    entity.freightTypeName = builder.freightTypeName;
    entity.objectId = builder.objectId;
    entity.objectType = builder.objectType;
    entity.objectName = builder.objectName;
    entity.doNotUse = builder.doNotUse ?? false
    entity.createDttm = builder.createDttm;
    entity.lastModifiedDttm = builder.lastModifiedDttm;
    return entity;
  }

  static from(plain: Meter): MeterEntity {
    return plainToInstance(MeterEntity, plain, {
      excludeExtraneousValues: true,
    });
  }

  loadPlainValue(): Promise<Meter> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(MeterValidator, object);
  }

  asPlainValue(): Meter {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true
    });
    return validateJSONNow(MeterValidator, object);
  }

  clone(): MeterEntity {
    return clone(this);
  }

  setUnitOfMeasure(value: string | UnitOfMeasureEntity) {
    if (typeof value === 'string') {
      this.unitOfMeasure = value;
    } else {
      this.unitOfMeasure = value.name;
    }
    return this;
  }

  setFreightType(value: string | FreightTypeEntity | undefined) {
    if (value) {
      if (typeof value === 'string') {
        this.freightTypeId = value;
        this.freightTypeName = undefined;
      } else {
        this.freightTypeId = value.id;
        this.freightTypeName = value.name;
      }
    } else {
      this.freightTypeId = undefined;
      this.freightTypeName = undefined;
    }
    return this;
  }

  setObject(value: EntityObject | undefined) {
    if (value) {
      if (isEntity(value)) {
        this.objectId = value.id;
        this.objectType = value.entityType;
        this.objectName = value.displayName;
      } else {
        this.objectId = value.objectId;
        this.objectType = value.objectType;
        this.objectName = value.objectName;
      }
    } else {
      this.objectId = undefined;
      this.objectType = undefined;
      this.objectName = undefined;
    }
    return this;
  }

  readonly CSV_HEADERS: string[] = [
    'Name',
    'Type',
    'Flow Meter Type',
    'Unit of Measure',
    'Freight Type',
    'Barcode',
    'Object',
    'Object Type',
  ];

  asCsv(): string[] {
    return [
      this.name ?? '',
      this.type.code ?? '',
      this.flowMeterType?.code ?? '',
      this.unitOfMeasure ?? '',
      this.freightTypeName ?? '',
      this.barcode ?? '',
      this.objectName ?? '',
      this.objectType ?? '',
    ];
  }
}

interface UnitOfMeasureStep {
  setUnitOfMeasure(value: string | UnitOfMeasureEntity): MeterBuilder;
}

interface TypeStep {
  setType(value: MeterType): UnitOfMeasureStep;
}

interface RegionStep {
  setRegion(value: string | RegionEntity): TypeStep;
}

interface NameStep {
  setName(value: string): RegionStep;
}

export class MeterBuilder
  implements NameStep, RegionStep, TypeStep, UnitOfMeasureStep {
  id: string;
  name!: string;
  regionId!: string;
  type!: MeterType;
  unitOfMeasure!: string;
  flowMeterType?: FlowMeterType;
  barcode?: string;
  freightTypeId?: string;
  freightTypeName?: string;
  objectId?: string;
  objectType?: string;
  objectName?: string;
  doNotUse?: boolean;
  createDttm?: number = undefined;
  lastModifiedDttm?: number = undefined;

  constructor(id: string = uuid()) {
    this.id = id;
  }

  setName(value: string): RegionStep {
    this.name = value;
    return this;
  }

  setRegion(value: string | RegionEntity): TypeStep {
    this.regionId = getRequiredEntityId(value);
    return this;
  }

  setType(value: MeterType): UnitOfMeasureStep {
    this.type = value;
    return this;
  }

  setUnitOfMeasure(value: string | UnitOfMeasureEntity): MeterBuilder {
    if (typeof value === 'string') {
      this.unitOfMeasure = value;
    } else {
      this.unitOfMeasure = value.name;
    }
    return this;
  }

  setFlowMeterType(value: FlowMeterType | undefined): MeterBuilder {
    this.flowMeterType = value;
    return this;
  }

  setBarcode(value: string | undefined): MeterBuilder {
    this.barcode = value;
    return this;
  }

  setFreightType(value: string | FreightTypeEntity | undefined): MeterBuilder {
    if (value) {
      if (typeof value === 'string') {
        this.freightTypeId = value;
        this.freightTypeName = undefined;
      } else {
        this.freightTypeId = value.id;
        this.freightTypeName = value.name;
      }
    } else {
      this.freightTypeId = undefined;
      this.freightTypeName = undefined;
    }
    return this;
  }

  setObject(value: EntityObject | undefined): MeterBuilder {
    if (value) {
      if (isEntity(value)) {
        this.objectId = value.id;
        this.objectType = value.entityType;
        this.objectName = value.displayName;
      } else {
        this.objectId = value.objectId;
        this.objectType = value.objectType;
        this.objectName = value.objectName;
      }
    } else {
      this.objectId = undefined;
      this.objectType = undefined;
      this.objectName = undefined;
    }
    return this;
  }

  setDoNotUse(value: boolean | undefined): MeterBuilder {
    this.doNotUse = value;
    return this;
  }

  setCreateDttm(value: number | undefined): MeterBuilder {
    this.createDttm = value;
    return this;
  }

  setLastModifiedDttm(value: number | undefined): MeterBuilder {
    this.lastModifiedDttm = value;
    return this;
  }

  build(): MeterEntity {
    return MeterEntity.create(this);
  }
}

export const MeterImportResultsValidator = t.intersection([
  t.type({
    meters: t.array(MeterValidator),
    missingMappings: t.array(t.string),
  }),
  t.partial({
    // partial props here
  }),
]);

export type MeterImportResults = t.TypeOf<typeof MeterImportResultsValidator>;
